import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Button,
  Tooltip,
  IconButton,
  CircularProgress,
  CardHeader,
  Link
} from '@mui/material';
import {
  Article as ArticleIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  ViewCarousel as CarouselIcon,
  Description as DocumentIcon,
  OpenInNew as OpenInNewIcon,
  Comment as CommentIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  AutoFixHigh as RewriteIcon,
  PictureAsPdf as PdfIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/build/pdf.worker.entry';

// Import SVG as ReactComponent
// Assuming post-reactions.svg is in a relative path like ../assets/ or similar
import { ReactComponent as PostReactions } from '../pages/post-reactions.svg';

// Placeholder for PostReactions if SVG import is problematic
// const PostReactions = () => <Box component="span">👍</Box>;

// Create a separate PDFPreview component
const PDFPreview = React.memo(({ url, onOpenPdf }) => {
  const [pdfImage, setPdfImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchAndRenderPDF = async () => {
      if (!url) return;
      
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(url);
        if (!isMounted) return;

        const blob = await response.blob();
        if (!isMounted) return;

        const arrayBuffer = await blob.arrayBuffer();
        if (!isMounted) return;

        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        if (!isMounted) return;
        
        const page = await pdf.getPage(1);
        if (!isMounted) return;
        
        const scale = 1.5;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({
          canvasContext: context,
          viewport
        }).promise;
        
        if (!isMounted) return;

        const image = canvas.toDataURL();
        setPdfImage(image);
      } catch (error) {
        if (isMounted) {
          console.error('Error rendering PDF:', error);
          setError('Failed to load PDF preview');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAndRenderPDF();

    return () => {
      isMounted = false;
      setPdfImage(null);
      setLoading(false);
      setError(null);
    };
  }, [url]);

  const containerStyle = useMemo(() => ({
    mb: 2,
    borderRadius: 2,
    overflow: 'hidden',
    border: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8f9fa',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f0f0f0',
      borderColor: '#d0d0d0'
    }
  }), []);

  if (!url) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No document available
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      onClick={() => onOpenPdf?.(url)}
      sx={containerStyle}
    >
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <PdfIcon sx={{ color: '#e94444' }} />
        <Typography variant="subtitle2">
          PDF Document
        </Typography>
        <OpenInNewIcon 
          sx={{ 
            ml: 'auto', 
            fontSize: '1rem', 
            color: '#666'
          }} 
        />
      </Box>

      <Box sx={{ 
        position: 'relative',
        minHeight: 200,
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {loading && (
          <Box sx={{ 
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1
          }}>
            <CircularProgress size={24} />
            <Typography variant="caption" color="text.secondary">
              Loading preview...
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <DocumentIcon sx={{ fontSize: 40, color: '#666', mb: 1 }} />
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}

        {!loading && !error && pdfImage && (
          <Box sx={{ 
            width: '100%', 
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            p: 2
          }}>
            <img 
              src={pdfImage} 
              alt="PDF preview"
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                objectFit: 'contain',
                borderRadius: 4
              }}
            />
          </Box>
        )}

        {!loading && !error && !pdfImage && (
          <Box sx={{ 
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2
          }}>
            <DocumentIcon sx={{ fontSize: 40, color: '#666' }} />
            <Typography variant="body2" color="text.secondary">
              Click to open PDF
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
});

PDFPreview.displayName = 'PDFPreview';

const EngagementPostCard = ({ 
  post, 
  onCommentClick,
  onRemixClick
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handlePdfClick = useCallback((url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const formatNumber = (num) => {
    const number = parseInt(num, 10);
    if (isNaN(number)) return 0;
    if (number >= 1000000) return (number / 1000000).toFixed(1) + 'M';
    if (number >= 1000) return (number / 1000).toFixed(1) + 'k';
    return number;
  };

  const getPostUrl = () => {
    if (post.zFull?.post_url) return post.zFull.post_url;
    if (post.url) return post.url;
    if (post.id && post.id.startsWith('urn:li:activity:')) {
      return `https://www.linkedin.com/feed/update/${post.id}`;
    }
    return `https://www.linkedin.com/feed/update/urn:li:activity:${post.idShort || post.id}`;
  };

  const handleSeeMoreClick = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const formatPostDate = (postDateStr, createdAtStr, zFullPostedTime) => {
    if (postDateStr && typeof postDateStr === 'string' && postDateStr.trim() !== '') {
        if (postDateStr.toLowerCase().includes('ago') || 
            postDateStr.toLowerCase().includes('yesterday') || 
            postDateStr.toLowerCase().includes('just now') || 
            postDateStr.match(/^\d{1,2} (second|minute|hour|day|week|month)s? ago$/i) ||
            !isNaN(new Date(postDateStr).getTime()) ) { 
             return postDateStr;
        }
    }
    const dateToParse = zFullPostedTime || createdAtStr;
    if (dateToParse) {
      try {
        const date = new Date(dateToParse.replace(' ', 'T'));
        if (isNaN(date.getTime())) throw new Error('Invalid date from zFull.posted or created_at');
        const now = new Date();
        const diffSeconds = Math.round((now.getTime() - date.getTime()) / 1000);
        const diffMinutes = Math.round(diffSeconds / 60);
        const diffHours = Math.round(diffMinutes / 60);
        const diffDays = Math.round(diffHours / 24);
        if (diffSeconds < 5) return 'Just now';
        if (diffSeconds < 60) return `${diffSeconds}s ago`;
        if (diffMinutes < 60) return `${diffMinutes}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch (error) {
        console.error('Error parsing date:', { postDateStr, createdAtStr, zFullPostedTime }, error);
      }
    }
    return 'Date unavailable';
  };

  const formattedDate = useMemo(() => {
    return formatPostDate(post.postDate, post.created_at, post.zFull?.posted);
  }, [post.postDate, post.created_at, post.zFull?.posted]);

  const handleViewClick = () => {
    const urlToOpen = getPostUrl();
    if (urlToOpen) window.open(urlToOpen, '_blank', 'noopener,noreferrer');
  };
  
  console.log('EngagementPostCard rendering post:', post.id, 'Format:', post.format, 'Media:', post.media);

  let derivedMediaType = 'text';
  let mediaUrl = null;
  let thumbnailUrl = null;
  let carouselItems = [];

  if (post.zFull?.content) {
    const content = post.zFull.content;
    if (content['com.linkedin.voyager.feed.render.ImageComponent']?.images?.[0]?.attributes?.[0]?.vectorImage?.rootUrl) {
      derivedMediaType = 'image';
      mediaUrl = content['com.linkedin.voyager.feed.render.ImageComponent'].images[0].attributes[0].vectorImage.rootUrl;
      thumbnailUrl = mediaUrl;
    } else if (content['com.linkedin.voyager.feed.render.VideoComponent']) {
      derivedMediaType = 'video';
      console.warn('VideoComponent found in zFull.content, but URL/thumbnail extraction path is not yet defined.', content['com.linkedin.voyager.feed.render.VideoComponent']);
    } else if (content['com.linkedin.voyager.feed.render.DocumentComponent']) {
      derivedMediaType = 'document';
      console.warn('DocumentComponent found in zFull.content, but URL extraction path is not yet defined.', content['com.linkedin.voyager.feed.render.DocumentComponent']);
    } else if (content['com.linkedin.voyager.feed.render.ArticleComponent']) {
        derivedMediaType = 'article';
        console.warn('ArticleComponent found in zFull.content, but URL extraction path is not yet defined.', content['com.linkedin.voyager.feed.render.ArticleComponent']);
    }
  }
  
  if (derivedMediaType === 'text' && post.zFull?.images?.length > 0) {
    derivedMediaType = 'image';
    mediaUrl = post.zFull.images[0].url;
    thumbnailUrl = mediaUrl;
    if (post.zFull.images.length > 1) {
        console.log('post.zFull.images has multiple items, potentially a simple carousel. Displaying first image.');
    }
  }

  console.log(`EngagementPostCard ID: ${post.idShort || post.id}, Derived Media Type: ${derivedMediaType}, Media URL: ${mediaUrl}, Thumbnail: ${thumbnailUrl}`);

  const renderPreview = useCallback(() => {
    const isDocument = derivedMediaType === 'document' || derivedMediaType === 'article';
    const isVideo = derivedMediaType === 'video';
    const isImage = derivedMediaType === 'image';
    const isCarousel = derivedMediaType === 'carousel_album';

    if (isDocument && mediaUrl) {
      return <PDFPreview url={mediaUrl} onOpenPdf={handlePdfClick} />;
    }
    if (isImage && thumbnailUrl) {
      return (
        <Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0', display: 'flex', justifyContent: 'center' }}>
          <img src={thumbnailUrl} alt="Post content" style={{ width: '100%', maxHeight: '600px', objectFit: 'contain', display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />
        </Box>
      );
    }
    if (isVideo && mediaUrl) {
      return (
        <Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0', backgroundColor: '#000' }}>
          <video controls playsInline preload="metadata" width="100%" style={{ display: 'block', maxHeight: '600px', objectFit: 'contain' }} poster={thumbnailUrl}>
            <source src={mediaUrl} type="video/mp4" />
            Your browser does not support HTML5 video.
          </video>
        </Box>
      );
    }
    return null;
  }, [derivedMediaType, mediaUrl, thumbnailUrl, handlePdfClick]);

  const getIconForPostType = () => {
    if (derivedMediaType === 'document' || derivedMediaType === 'article') return <DocumentIcon fontSize="small" sx={{ color: '#0073b1' }} />;
    if (derivedMediaType === 'video') return <VideoIcon fontSize="small" sx={{ color: '#0073b1' }} />;
    if (derivedMediaType === 'image') return <ImageIcon fontSize="small" sx={{ color: '#0073b1' }} />;
    if (derivedMediaType === 'carousel_album') return <CarouselIcon fontSize="small" sx={{ color: '#0073b1' }} />;
    return <ArticleIcon fontSize="small" sx={{ color: '#0073b1' }} />;
  };
  
  const likes = post.numLikes ?? post.zFull?.num_likes ?? 0;
  const comments = post.numComments ?? post.zFull?.num_comments ?? 0;
  const shares = post.totalShares ?? post.zFull?.num_reposts ?? 0;
  const authorName = post.user?.name || post.zFull?.poster?.first + ' ' + post.zFull?.poster?.last || 'LinkedIn User';
  const authorProfilePic = post.user?.profile_image_url_https || post.user?.image || post.zFull?.resharerAccount?.image || '';
  const authorProfileUrl = post.user?.username ? `https://www.linkedin.com/in/${post.user.username}` : post.zFull?.poster_linkedin_url;
  const postContent = post.text || post.zFull?.text || '';

  return (
    <Card 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          transform: 'translateY(-2px)'
        },
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid var(--border-color)',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}
    >
      <CardHeader
        avatar={
          <Avatar 
            src={authorProfilePic} 
            alt={authorName}
            onClick={() => authorProfileUrl && window.open(authorProfileUrl, '_blank')}
            sx={{ cursor: authorProfileUrl ? 'pointer' : 'default' }}
          />
        }
        title={
          <Link href={authorProfileUrl || undefined} target="_blank" rel="noopener noreferrer" variant="subtitle2" component="span" fontWeight="600" sx={{ textDecoration: 'none', color: 'inherit', cursor: authorProfileUrl ? 'pointer' : 'default', '&:hover': { textDecoration: authorProfileUrl ? 'underline' : 'none'} }}>
            {authorName}
          </Link>
        }
        subheader={
          <Box sx={{ maxWidth: '100%' }}>
            {(post.zFull?.poster?.headline || post.user?.title) && (
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  display: 'block', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  maxWidth: '100%',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  display: '-webkit-box',
                  lineHeight: 1.2,
                  mb: 0.3
                }}
              >
                {post.zFull?.poster?.headline || post.user?.title}
              </Typography>
            )}
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                display: 'block',
                lineHeight: 1.2,
                opacity: 0.8
              }}
            >
              {formattedDate}
            </Typography>
          </Box>
        }
        action={
            <Tooltip title={derivedMediaType ? derivedMediaType.charAt(0).toUpperCase() + derivedMediaType.slice(1) : 'Post'}>
                {getIconForPostType()} 
            </Tooltip>
        }
        sx={{ p: 2, pb: 0}}
      />
      <CardContent sx={{ p: 2, pt: 1, flex: 1 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            mb: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: isExpanded ? 'unset' : 5,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.5,
            color: '#000000',
            fontSize: '0.9rem',
            whiteSpace: 'pre-line',
            transition: 'all 0.3s ease-in-out',
            maxHeight: isExpanded ? 'none' : '7.5em',
            width: '100%',
            maxWidth: '100%',
            wordBreak: 'break-word'
          }}
        >
          {postContent}
        </Typography>

        {(postContent && postContent.length > 180) && (
            <Typography 
            variant="body2" 
            onClick={handleSeeMoreClick}
            sx={{ 
                color: '#0a66c2', 
                mb: 2,
                cursor: 'pointer',
                fontWeight: 500,
                display: 'inline-block',
                transition: 'opacity 0.2s ease',
                '&:hover': {
                opacity: 0.8
                }
            }}
            >
            {isExpanded ? 'See less' : 'See more'}
            </Typography>
        )}
        
        {renderPreview()}
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mt: 'auto', 
          pt: 1,
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          width: '100%'
        }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              backgroundColor: '#f7f9fa',
              borderRadius: '12px',
              padding: '2px 8px',
              border: '1px solid #e0e0e0',
              marginBottom: '4px',
              marginRight: '4px'
            }}
          >
            <PostReactions width={36} height={16} />
            <Typography variant="caption" sx={{ ml: 0.5, fontSize: '0.8rem', color: '#666' }}>
              {formatNumber(likes)}
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            flexWrap: 'wrap',
            marginBottom: '4px'
          }}>
            <Typography variant="caption" sx={{ 
              color: '#666', 
              fontSize: '0.8rem', 
              display: 'flex', 
              alignItems: 'center',
              whiteSpace: 'nowrap'
            }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: '0.9rem', mr: 0.3 }} />
              {formatNumber(comments)} comments
            </Typography>
            
            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.8rem', display: { xs: 'none', sm: 'block' } }}>
              •
            </Typography>
            
            <Typography variant="caption" sx={{ 
              color: '#666', 
              fontSize: '0.8rem',
              whiteSpace: 'nowrap',
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center'
            }}>
              <ShareIcon sx={{ fontSize: '0.9rem', mr: 0.3 }} />
              {formatNumber(shares)} shares
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <CardActions sx={{ 
        px: 2, 
        py: 1, 
        borderTop: '1px solid #f0f0f0', 
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          flexWrap: 'nowrap'
        }}>
          <Tooltip title="Comment">
            <Button
              size="small"
              sx={{ 
                textTransform: 'none', 
                color: 'var(--primary-color)',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(10, 102, 194, 0.08)'
                },
                minWidth: { xs: '32px', sm: '80px' },
                p: { xs: '4px', sm: '4px 8px' },
                '& .MuiButton-startIcon': {
                  mr: { xs: 0, sm: 0.5 }
                }
              }}
              onClick={() => onCommentClick?.(post)}
              startIcon={<CommentIcon fontSize="small" />}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Comment</Box>
            </Button>
          </Tooltip>

          <Tooltip title="Remix">
            <Button
              size="small"
              sx={{ 
                textTransform: 'none', 
                color: 'var(--primary-color)',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(10, 102, 194, 0.08)'
                },
                minWidth: { xs: '32px', sm: '70px' },
                p: { xs: '4px', sm: '4px 8px' },
                '& .MuiButton-startIcon': {
                  mr: { xs: 0, sm: 0.5 }
                }
              }}
              onClick={() => onRemixClick?.(post)}
              startIcon={<RewriteIcon fontSize="small" />}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Remix</Box>
            </Button>
          </Tooltip>
        </Box>
        
        <Tooltip title="View on LinkedIn">
          <Button 
            size="small"
            sx={{ 
              textTransform: 'none', 
              color: 'var(--primary-color)',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(10, 102, 194, 0.08)'
              },
              minWidth: { xs: '32px', sm: '60px' },
              p: { xs: '4px', sm: '4px 8px' },
              '& .MuiButton-startIcon': {
                mr: { xs: 0, sm: 0.5 }
              }
            }}
            onClick={handleViewClick}
            startIcon={<OpenInNewIcon fontSize="small" />}
          >
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>View</Box>
          </Button>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

export default EngagementPostCard; 